import { memo, useCallback, useEffect } from 'react'
import { Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import store from '../../store'
import commonActions from '../../actions/common.actions'
import { FeedbackCategory } from '../../proto/generated/user-feedback'
import { setCategory } from '../../reducer/feedback.reducer'
import { setSteps } from '../../reducer/tour.reducer'
import { setSupportView } from '../../reducer/supportPanel.reducer'
import { selectSupportView } from '../../selectors/supportPanel'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import HeadsetIcon from '../../assets/icons/HeadsetIcon'
import { useRouteTourSteps } from '../tour/useRouteTourSteps'
import SupportHeader from './SupportHeader'
import SupportScrollArea from './SupportScrollArea'
import SupportHome, { SupportIntent } from './SupportHome'
import FeedbackForm from './FeedbackForm'
import HelpCenterView from './HelpCenterView'
import ShortcutsView from './ShortcutsView'
import ContactView from './ContactView'

/**
 * Help & feedback sidebar tab. One entry point for bug reports, feedback, the guided tour,
 * the help center (with keyboard shortcuts) and contact details.
 */
function SupportPanelInner() {
    const dispatch = useDispatch()
    const view = useSelector(selectSupportView)
    const tourSteps = useRouteTourSteps()

    // Start over next time the tab is opened, unless it is only hidden for a screenshot or
    // recording, in which case the capture layer brings it back to the same screen.
    useEffect(
        () => () => {
            if (!store.getState().feedbackStore.isCapturing) store.dispatch(setSupportView('home'))
        },
        []
    )

    const goHome = useCallback(() => dispatch(setSupportView('home')), [dispatch])
    const openHelp = useCallback(() => dispatch(setSupportView('help')), [dispatch])
    const openShortcuts = useCallback(() => dispatch(setSupportView('shortcuts')), [dispatch])
    const openComposer = useCallback(
        (category: FeedbackCategory) => {
            dispatch(setCategory(category))
            dispatch(setSupportView(category === FeedbackCategory.BUG ? 'bug' : 'feedback'))
        },
        [dispatch]
    )

    const onIntent = useCallback(
        (intent: SupportIntent) => {
            AnalyticsManager.logEvent('Support Intent Selected', { intent })
            switch (intent) {
                case 'bug':
                    openComposer(FeedbackCategory.BUG)
                    break
                case 'feedback':
                    openComposer(FeedbackCategory.FEEDBACK)
                    break
                case 'tour':
                    if (tourSteps.length === 0) return
                    AnalyticsManager.logEvent('Tour Started', { source: 'support-panel', steps: tourSteps.length })
                    dispatch(setSteps(tourSteps))
                    // The tour walks the page itself, so give it the full width back.
                    commonActions.dispatchRightSideWidget(dispatch, null)
                    break
                case 'help':
                    openHelp()
                    break
                case 'contact':
                    dispatch(setSupportView('contact'))
                    break
            }
        },
        [dispatch, openComposer, openHelp, tourSteps]
    )

    return (
        <Stack sx={{ height: '100%', minHeight: 0 }} data-testid="support-panel" data-support-view={view}>
            {view === 'home' ? (
                <>
                    <SupportHeader title="Help & feedback" icon={<HeadsetIcon sx={{ fontSize: 24 }} />} />
                    <SupportScrollArea>
                        <SupportHome onSelect={onIntent} tourAvailable={tourSteps.length > 0} />
                    </SupportScrollArea>
                </>
            ) : view === 'bug' || view === 'feedback' ? (
                <FeedbackForm onBack={goHome} onDone={goHome} />
            ) : view === 'help' ? (
                <HelpCenterView onBack={goHome} onOpenShortcuts={openShortcuts} />
            ) : view === 'shortcuts' ? (
                <ShortcutsView onBack={openHelp} />
            ) : (
                <ContactView onBack={goHome} onWriteToUs={() => openComposer(FeedbackCategory.FEEDBACK)} />
            )}
        </Stack>
    )
}

export default memo(SupportPanelInner)
